import { type ReactNode, type TouchEvent, type WheelEvent } from 'react';
import { Button } from '@zep-us/zus';
import { OpacityVioletColors } from '@zep-us/zus/tokens';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/utils';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { type AiBuilderGenerationOverlayMode } from '../hooks/useAiBuilderQuestionMutationLock';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
export const preventAiBuilderShellScroll = (event: WheelEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
  event.preventDefault();
  event.stopPropagation();
};
export const getAiBuilderGenerationOverlayCopy = (mode: AiBuilderGenerationOverlayMode, copy: AiBuilderChatCopy) => {
  if (mode === 'applying') return copy.generationOverlay.applying;
  return copy.generationOverlay.generating;
};

/**
 * 생성 잠금 오버레이 — 워시 배경/바이올렛 프레임(visualVisible로 페이드 인)과
 * 인터랙션 차단막을 깔고, 하단 상태 툴바는 children 컴포지션으로 받는다.
 * 부모(모프 프레임)의 border-radius를 상속하므로 fixed 카드 안에서만 사용한다.
 *
 * @example
 * <AiBuilderGenerationLockOverlay visualVisible={visible}>
 *   <AiBuilderGenerationLockOverlay.StatusToolbar onCancel={cancel}>
 *     문제를 생성 중입니다
 *   </AiBuilderGenerationLockOverlay.StatusToolbar>
 * </AiBuilderGenerationLockOverlay>
 */
export const AiBuilderGenerationLockOverlay = ({
  children,
  visualVisible
}: {
  children: ReactNode;
  visualVisible: boolean;
}) => {
  return <>
      <div aria-hidden="true" data-ai-generation-wash className={cn('pointer-events-none absolute inset-0 z-[56] overflow-hidden rounded-[inherit]', AI_GENERATION_VISUAL_TRANSITION_CLASS, visualVisible ? 'opacity-100' : 'opacity-0')}>
        <AiBuilderGenerationWashWave data-sentry-element="AiBuilderGenerationWashWave" data-sentry-source-file="AiBuilderGenerationLockOverlay.tsx" />
      </div>
      <div aria-hidden="true" data-ai-generation-border className={cn('pointer-events-none absolute inset-0 z-[60] rounded-[inherit] border-[3px] border-opacity-violet-4', AI_GENERATION_VISUAL_TRANSITION_CLASS, visualVisible ? 'opacity-100' : 'opacity-0')} />
      <div aria-hidden="true" data-ai-generation-interaction-lock="true" className="pointer-events-auto absolute inset-0 z-[58] cursor-wait rounded-[inherit] bg-transparent" onTouchMove={preventAiBuilderShellScroll} onWheel={preventAiBuilderShellScroll} />
      {children}
    </>;
};
const AiBuilderGenerationStatusToolbar = ({
  children,
  onCancel
}: {
  children: ReactNode;
  onCancel: () => void;
}) => {
  const copy = useAiBuilderChatCopy();
  return <div className="pointer-events-auto absolute bottom-2xl left-1/2 z-[59] flex min-h-[48px] max-w-[calc(100%-32px)] -translate-x-1/2 items-center gap-xs rounded-sm bg-opacity-white-10 p-sm shadow-lg backdrop-blur-[2px]" role="status" aria-live="polite" data-sentry-component="AiBuilderGenerationStatusToolbar" data-sentry-source-file="AiBuilderGenerationLockOverlay.tsx">
      <span aria-hidden="true" className="size-lg flex-none animate-spin rounded-full border-[3px] border-gray-400 border-t-transparent" />
      <span className="min-w-0 truncate whitespace-nowrap pr-lg text-body-lg-semibold text-default">
        {children}
      </span>
      <Button type="button" variant="outlined" intent="neutral" size="sm" className="flex-none" onClick={onCancel} data-sentry-element="Button" data-sentry-source-file="AiBuilderGenerationLockOverlay.tsx">
        {copy.generationOverlay.cancel}
      </Button>
    </div>;
};
AiBuilderGenerationLockOverlay.StatusToolbar = AiBuilderGenerationStatusToolbar;

/**
 * 워시 웨이브 — 135deg 반복 그라디언트의 background-position을 정확히
 * 한 주기만큼 선형 이동시키는 루프라, 이음새 없이 한 방향(우하단)으로
 * 계속 흐르는 것처럼 보인다(끝 색과 시작 색이 같아 랩이 보이지 않는다).
 */
const AiBuilderGenerationWashWave = () => {
  const shouldReduceMotion = useReducedMotion();
  return <motion.div aria-hidden="true" className="absolute inset-0" style={{
    backgroundImage: AI_GENERATION_WASH_BACKGROUND,
    backgroundSize: `${AI_GENERATION_WASH_WAVE_TILE_PX}px ${AI_GENERATION_WASH_WAVE_TILE_PX}px`
  }} animate={shouldReduceMotion ? undefined : {
    backgroundPosition: ['0px 0px', `${AI_GENERATION_WASH_WAVE_SHIFT_PX}px ${AI_GENERATION_WASH_WAVE_SHIFT_PX}px`]
  }} transition={{
    duration: AI_GENERATION_WASH_WAVE_DURATION_S,
    ease: 'linear',
    repeat: Infinity
  }} data-sentry-element="unknown" data-sentry-component="AiBuilderGenerationWashWave" data-sentry-source-file="AiBuilderGenerationLockOverlay.tsx" />;
};
const AI_GENERATION_VISUAL_TRANSITION_CLASS = 'transition-opacity duration-[220ms] ease-out motion-reduce:duration-[1ms]';
const AI_GENERATION_WASH_WAVE_DURATION_S = 5;

// 바이올렛 밴드 한 주기의 길이(135deg 축 기준).
// 화면 대각선보다 길게 잡아 한 화면에 밴드 전환이 하나만 보이게 한다 —
// 주기가 짧으면 줄무늬가 여러 개 생겨 면이 분절되어 보인다.
const AI_GENERATION_WASH_WAVE_PERIOD_PX = 1800;

// 135deg 축으로 정확히 한 주기가 되는 x/y 평행이동량 = period / √2
const AI_GENERATION_WASH_WAVE_SHIFT_PX = (AI_GENERATION_WASH_WAVE_PERIOD_PX / Math.SQRT2).toFixed(2);

// 배경 타일 한 변의 길이 = period × √2.
// 그라디언트 이미지는 background-size 크기로 래스터된 뒤 타일되는데,
// 정사각 타일의 x/y 이동을 135deg 축에 사영하면 변길이/√2 = 정확히 한
// 주기가 되어 타일 경계에서 위상이 이어진다(명시하지 않으면 요소 크기로
// 타일되어 background-position 이동 시 이음새가 드러난다).
const AI_GENERATION_WASH_WAVE_TILE_PX = (AI_GENERATION_WASH_WAVE_PERIOD_PX * Math.SQRT2).toFixed(2);

// 바이올렛 밴드가 주기에서 차지하는 폭의 비율(시안 37421:36872 기준 —
// 표면 대부분은 거의 흰색이고 바이올렛은 좁은 밴드로만 흐른다).
const AI_GENERATION_WASH_BAND_RATIO = 0.4;
const AI_GENERATION_WASH_BAND_START_PX = AI_GENERATION_WASH_WAVE_PERIOD_PX * (1 - AI_GENERATION_WASH_BAND_RATIO) / 2;
const AI_GENERATION_WASH_BAND_END_PX = AI_GENERATION_WASH_WAVE_PERIOD_PX * (1 + AI_GENERATION_WASH_BAND_RATIO) / 2;

// Figma 37421:36872의 바이올렛 워시를 무한 흐름용 반복 패턴으로 변환한 것.
// 주기의 70%는 거의 흰색인 골짜기(violet/4)로 유지되고, 두드러진 피크
// (violet/40)는 좁은 밴드로만 지나가 흰 영역이 넓게 남는다.
const AI_GENERATION_WASH_BACKGROUND = `repeating-linear-gradient(135deg, ${OpacityVioletColors[0]} 0px, ${OpacityVioletColors[0]} ${AI_GENERATION_WASH_BAND_START_PX}px, ${OpacityVioletColors[4]} ${AI_GENERATION_WASH_WAVE_PERIOD_PX / 2}px, ${OpacityVioletColors[0]} ${AI_GENERATION_WASH_BAND_END_PX}px, ${OpacityVioletColors[0]} ${AI_GENERATION_WASH_WAVE_PERIOD_PX}px)`;